import { ApolloClient, from, HttpLink, InMemoryCache } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { type ClientConfigurationFn, createNhostClient, type NhostClient } from '@nhost/nhost-js'
import {
  attachAccessTokenMiddleware,
  type ChainFunction,
  sessionRefreshMiddleware,
  updateSessionFromResponseMiddleware,
} from '@nhost/nhost-js/fetch'

/**
 * The one nhost client: sign-in and session (nhost/auth) and files
 * (nhost/storage). Both are served on the app's own origin — by Caddy in
 * production, by Vite's proxy in development — so there is nothing to
 * configure and no CORS.
 *
 * The session (access + refresh token) lives in localStorage under
 * `nhostSession`; the client refreshes it before it expires.
 */
const origin = window.location.origin

/** Requests to nhost/auth give up after this long. */
const AUTH_TIMEOUT_MS = 15_000

/**
 * nhost-js renews the access token under an exclusive Web Lock and makes the
 * refresh request while holding it. Its default setup runs every auth request
 * through a "refresh the session first" step, which asks for the same lock —
 * and only skips the refresh request itself when the URL ends in `/v1/token`,
 * nhost cloud's layout. Ours is `/auth/token`, so the first renewal (about
 * fifteen minutes after sign-in) deadlocked: nothing loaded any more, and a
 * reload hung on the loading screen without sending a request.
 *
 * So the session middleware is set up here as createClient() would, except
 * that auth requests skip the refresh step (they never need it: sign-in and
 * renewal bring their own tokens). Auth requests also time out: the lock is
 * held while one is in flight, and a request stalled on the network would
 * otherwise block every open tab of the app.
 */
const withTimeout =
  (ms: number): ChainFunction =>
  (next) =>
  (url, options = {}) =>
    next(url, { ...options, signal: options.signal ?? AbortSignal.timeout(ms) })

const sessionMiddleware: ClientConfigurationFn = ({
  auth,
  storage,
  graphql,
  functions,
  sessionStorage,
}) => {
  const refresh = sessionRefreshMiddleware(auth, sessionStorage)
  const update = updateSessionFromResponseMiddleware(sessionStorage)
  const attach = attachAccessTokenMiddleware(sessionStorage)
  for (const client of [storage, graphql, functions]) {
    for (const mw of [refresh, update, attach]) client.pushChainFunction(mw)
  }
  for (const mw of [withTimeout(AUTH_TIMEOUT_MS), update, attach]) auth.pushChainFunction(mw)
}

export const nhost: NhostClient = createNhostClient({
  authUrl: `${origin}/auth`,
  storageUrl: `${origin}/storage/v1`,
  graphqlUrl: import.meta.env.REACT_APP_GRAPHQL_URI,
  configure: [sessionMiddleware],
})

/**
 * A valid access token, refreshed first when it has less than a minute left.
 * Null when signed out.
 */
export const accessToken = async (): Promise<string | null> => {
  const session = await nhost.refreshSession(60)
  return session?.accessToken ?? null
}

/** Hasura claims of the current session, or undefined when signed out. */
export const hasuraClaims = (): Record<string, unknown> | undefined =>
  nhost.getUserSession()?.decodedToken?.['https://hasura.io/jwt/claims'] as
    Record<string, unknown> | undefined

/**
 * Apollo against Hasura with the session's token. No websocket link: nothing in
 * the app subscribes.
 */
export const createApolloClient = (graphqlURL: string) =>
  new ApolloClient({
    link: from([
      setContext(async (_, { headers }) => {
        const token = await accessToken()
        return {
          headers: { ...headers, ...(token ? { authorization: `Bearer ${token}` } : {}) },
        }
      }),
      new HttpLink({ uri: graphqlURL }),
    ]),
    cache: new InMemoryCache(),
  })
